import { useEffect, useRef, useState } from "react";
import { BosDurum, Buton, Iskelet, Kart, NotKutusu, Rozet, cn } from "@koclukpanel/ui";
import { SIPARIS_HESAP_DURUMU } from "@koclukpanel/contracts";
import { ApiHatasi } from "./api";
import { magazaVitrini, siparisVer } from "./api-magaza";
import { hataMetni } from "./hata-metni";
import {
  SIPARIS_DURUMU_ETIKETI,
  type MagazaUrunu,
  type MagazaVitrini,
  type Siparis,
} from "./magaza-tipleri";
import { AdresBolumu, gunBicimi, useUsteKaydir } from "./SiparisDetay";

/**
 * MAĞAZA — F-GAM-16/17, oyunlaştırma dalga 2 / sürüm 7a (öğrenci tarafı).
 *
 * Ekran HİÇBİR puan kararı vermez: bakiye, fiyat, stok (`tukendi`), 18 yaş ve sipariş durumu SUNUCUDAN gelir.
 * Tek hesap, "yetmiyorsa kaç puan eksik" cümlesi için fiyat − bakiye farkıdır; asıl kapı sunucudadır
 * (yetersiz puan ve stok yarışı orada 409 döner, ekran o metni gösterir).
 *
 * Emre kararları (dalga-2-tasarim.md §10): yalnız kargo (teslimat seçeneği YOK), stok bitince "Tükendi" ve sipariş
 * yok, kargoya verilene kadar iptal, 18 altında veli onayı + adres 7 gün.
 *
 * Emre 07.10.2026 (7b madde 7): yalnız kaydı açık (ACTIVE) öğrenci YENİ sipariş verir. Sunucu vitrinde
 * `siparisVerilemez` doldurursa ürünler ve fiyatlar görünür kalır, "Sipariş ver" kapanır ve SUNUCUNUN cümlesi
 * gösterilir (ekran kendi cümlesini üretmez). Durum vitrin açıkken değişirse sipariş isteği 409
 * `SIPARIS_HESAP_DURUMU` döner: aynı cümle gösterilir ve vitrin yeniden yüklenir. Siparişlerim, iptal ve adres
 * yolları bu kuraldan etkilenmez.
 */

/**
 * Sipariş istek kimliği: AYNI ağ hatasında yeniden gönderim AYNI siparişi döndürür, puan iki kez düşmez.
 * `randomUUID` yalnız güvenli bağlamda (https) var; yerel ağdaki geliştirme adresinde yedeği devreye girer.
 */
function yeniIstekKimligi(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === "function") return c.randomUUID();
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export interface MagazaProps {
  /** Sipariş sonrası "Siparişlerime git" düğmesi; verilmezse düğme çizilmez. */
  onSiparislerim?: () => void;
}

export function Magaza({ onSiparislerim }: MagazaProps = {}) {
  const [vitrin, setVitrin] = useState<MagazaVitrini | null>(null);
  const [kapali, setKapali] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [yenile, setYenile] = useState(0);
  const [secili, setSecili] = useState<MagazaUrunu | null>(null);
  const [sonuc, setSonuc] = useState<Siparis | null>(null);
  /**
   * Sipariş anında gelen 409 `SIPARIS_HESAP_DURUMU` cümlesi — vitrin yeniden yüklenene kadar köprü. Taze vitrin
   * gelince silinir: hesap durumu bilgisinin tek kaynağı vitrinin `siparisVerilemez` alanıdır (arada kayıt yeniden
   * açıldıysa eski cümle ekranda kalmaz).
   */
  const [anlikHesapMesaji, setAnlikHesapMesaji] = useState<string | null>(null);

  useEffect(() => {
    let iptal = false;
    void (async () => {
      try {
        const v = await magazaVitrini();
        if (!iptal) {
          setHata(null);
          setAnlikHesapMesaji(null);
          setVitrin(v);
        }
      } catch (e) {
        if (iptal) return;
        if (e instanceof ApiHatasi && e.durum === 404) setKapali(true);
        else setHata(hataMetni(e, "Mağaza alınamadı."));
      }
    })();
    return () => {
      iptal = true;
    };
  }, [yenile]);

  function vitrineDon(taze: boolean) {
    setSecili(null);
    setSonuc(null);
    setHata(null);
    // Sipariş sonrası bakiye ve stok değişti; eski vitrini bir an bile göstermemek için iskelet.
    if (taze) setVitrin(null);
    setYenile((n) => n + 1);
  }

  if (sonuc) {
    return (
      <SiparisSonucu
        siparis={sonuc}
        onGuncel={setSonuc}
        onMagaza={() => vitrineDon(true)}
        onSiparislerim={onSiparislerim}
      />
    );
  }
  if (kapali) return <p className="text-notr-600 text-sm">Mağaza şu an kapalı.</p>;
  if (hata && vitrin === null) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="rounded-alan bg-hata-50 text-hata-700 w-full px-3 py-2 text-sm">
          {hata}
        </p>
        <Buton
          gorunum="ikincil"
          onClick={() => {
            setHata(null);
            setYenile((n) => n + 1);
          }}
        >
          Tekrar dene
        </Buton>
      </div>
    );
  }
  if (vitrin === null) return <Iskelet satir={5} />;

  const hesapMesaji = vitrin.siparisVerilemez?.mesaj ?? anlikHesapMesaji;

  if (secili) {
    return (
      <UrunDetay
        key={secili.id}
        urun={secili}
        bakiye={vitrin.bakiye}
        hesapMesaji={hesapMesaji}
        onGeri={(yenilensin) => {
          setSecili(null);
          if (yenilensin) setYenile((n) => n + 1);
        }}
        onSiparis={setSonuc}
        onHesapDurumu={(mesaj) => {
          // Eski vitrin ekranda kalır (iskelet yok); taze vitrin gelince `siparisVerilemez` cümleyi devralır.
          setAnlikHesapMesaji(mesaj);
          setYenile((n) => n + 1);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Kart baslikSeviyesi={3} baslik="Puanın">
        <p className="text-notr-900 text-3xl font-semibold tabular-nums">{vitrin.bakiye}</p>
        <p className="text-notr-600 mt-1 text-sm">
          Bekleyen puanların Pazartesi kesinleşince buraya eklenir.
        </p>
      </Kart>

      {hesapMesaji !== null && <HesapDurumuNotu mesaj={hesapMesaji} />}

      {vitrin.urunler.length === 0 ? (
        <BosDurum
          baslik="Mağazada henüz ürün yok"
          aciklama="Yeni ürünler eklenince burada görünür."
        />
      ) : (
        <>
          <h3 className="sr-only">Ürünler</h3>
          <ul className="grid grid-cols-2 gap-3">
            {vitrin.urunler.map((u) => {
              const eksik = u.puanFiyati - vitrin.bakiye;
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => setSecili(u)}
                    className={cn(
                      "min-h-dokunma rounded-kart border-notr-200 bg-notr-0 shadow-kart flex h-full w-full",
                      "flex-col overflow-hidden border text-left transition-colors",
                      "hover:bg-notr-50 focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                    )}
                  >
                    <UrunGorseli urun={u} className="aspect-square w-full" />
                    <span className="flex flex-1 flex-col items-start gap-1 p-3">
                      <span className="text-notr-900 text-sm font-medium">{u.ad}</span>
                      <span className="text-notr-900 text-sm font-semibold tabular-nums">
                        {u.puanFiyati} puan
                      </span>
                      {u.tukendi ? (
                        <Rozet ton="uyari">Tükendi</Rozet>
                      ) : eksik > 0 ? (
                        <span className="text-notr-600 text-xs">{eksik} puan eksik</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

/** Hesap durumu yüzünden sipariş verilemiyor — sunucunun cümlesi; `status`: ekran okuyucu sessizce duyurur. */
function HesapDurumuNotu({ mesaj }: { mesaj: string }) {
  return (
    <div role="status">
      <NotKutusu ton="bilgi">{mesaj}</NotKutusu>
    </div>
  );
}

/** Görsel yoksa ürünün baş harfi: boş gri kutu "yüklenmedi" gibi okunuyordu. */
function UrunGorseli({ urun, className }: { urun: MagazaUrunu; className?: string }) {
  // Urun gorseli 7a sunucusunda YOK (gorsel yukleme sonraki adim): bas harf yer tutucusu.
  return (
    <span
      aria-hidden="true"
      className={cn(
        "bg-notr-100 text-notr-600 flex items-center justify-center text-3xl font-semibold",
        className,
      )}
    >
      {urun.ad.slice(0, 1).toLocaleUpperCase("tr-TR")}
    </span>
  );
}

function UrunDetay({
  urun,
  bakiye,
  hesapMesaji,
  onGeri,
  onSiparis,
  onHesapDurumu,
}: {
  urun: MagazaUrunu;
  bakiye: number;
  /** Doluysa öğrenci şu an YENİ sipariş veremez (hesap durumu); sunucunun cümlesi. */
  hesapMesaji: string | null;
  /** `true`: sipariş denendi ve hata aldı; vitrin (stok/bakiye) tazelensin. */
  onGeri: (yenilensin: boolean) => void;
  onSiparis: (s: Siparis) => void;
  /** Sipariş 409 `SIPARIS_HESAP_DURUMU` ile reddedildi: cümle gösterilsin, vitrin yeniden yüklensin. */
  onHesapDurumu: (mesaj: string) => void;
}) {
  const kok = useUsteKaydir();
  // İstek kimliği onay adımı İLK açılınca BİR KEZ üretilir ve bu ekran açık kaldıkça aynı kalır:
  // yanıtı kaybolan bir isteği yeniden denemek (ya da vazgeçip yeniden açmak) ikinci sipariş yaratmaz.
  const istekKimligi = useRef<string | null>(null);
  const [onay, setOnay] = useState(false);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const eksik = urun.puanFiyati - bakiye;

  function onayAc() {
    istekKimligi.current ??= yeniIstekKimligi();
    setHata(null);
    setOnay(true);
  }

  function ver() {
    if (gonderiliyor) return;
    const kimlik = (istekKimligi.current ??= yeniIstekKimligi());
    setGonderiliyor(true);
    setHata(null);
    siparisVer({ urunId: urun.id, istekKimligi: kimlik })
      .then(onSiparis)
      .catch((e: unknown) => {
        setGonderiliyor(false);
        if (e instanceof ApiHatasi && e.durum === 409 && e.kod === SIPARIS_HESAP_DURUMU) {
          // Sipariş OLMADI (sunucu stoktan ve harcamadan önce reddetti); istek kimliği tüketilmedi.
          setOnay(false);
          setHata(null);
          onHesapDurumu(e.message);
          return;
        }
        setHata(hataMetni(e, "Sipariş verilemedi. Tekrar dene."));
      });
  }

  return (
    <div ref={kok} className="flex flex-col gap-3">
      <div>
        <Buton gorunum="sessiz" disabled={gonderiliyor} onClick={() => onGeri(hata !== null)}>
          Mağazaya dön
        </Buton>
      </div>

      <Kart
        baslikSeviyesi={3}
        baslik={urun.ad}
        eylem={urun.tukendi ? <Rozet ton="uyari">Tükendi</Rozet> : undefined}
      >
        <UrunGorseli urun={urun} className="rounded-alan mb-3 aspect-[4/3] w-full" />
        {urun.aciklama && <p className="text-notr-700 mb-3 text-sm">{urun.aciklama}</p>}
        <p className="text-notr-900 text-2xl font-semibold tabular-nums">{urun.puanFiyati} puan</p>
        <p className="text-notr-600 mt-1 text-sm">
          Puanın: <span className="text-notr-900 font-medium tabular-nums">{bakiye}</span>
        </p>
        {urun.kisiBasiSinir !== null && (
          <p className="text-notr-600 mt-1 text-xs">
            Bir kişi en fazla {urun.kisiBasiSinir} adet sipariş verebilir.
          </p>
        )}
        <p className="text-notr-600 mt-1 text-xs">Ürün kargoyla gönderilir.</p>

        <div className="mt-4">
          {urun.tukendi ? (
            <NotKutusu ton="uyari">Bu ürün şu an tükendi.</NotKutusu>
          ) : hesapMesaji !== null ? (
            <>
              <Buton boyut="genis" disabled>
                Sipariş ver
              </Buton>
              <div className="mt-2">
                <HesapDurumuNotu mesaj={hesapMesaji} />
              </div>
            </>
          ) : onay ? (
            <section
              aria-labelledby="magaza-onay"
              className="rounded-alan border-marka-200 bg-marka-50 border p-3"
            >
              <h4 id="magaza-onay" className="text-notr-900 text-sm font-semibold">
                Siparişini onaylıyor musun?
              </h4>
              <ul className="text-notr-700 mt-2 list-disc space-y-1 pl-5 text-sm">
                <li>
                  {urun.ad} için {urun.puanFiyati} puan harcanır.
                </li>
                <li>Puan hemen düşer; kargoya verilene kadar siparişi iptal edebilirsin.</li>
                <li>
                  Teslimat kargoyla yapılır. 18 yaşından küçüksen siparişi velin onaylar ve adresi o
                  verir.
                </li>
              </ul>
              {hata && (
                <p
                  role="alert"
                  className="rounded-alan bg-hata-50 text-hata-700 mt-2 px-3 py-2 text-sm"
                >
                  {hata}
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Buton yukleniyor={gonderiliyor} yukleniyorMetni="Sipariş veriliyor" onClick={ver}>
                  Evet, sipariş ver
                </Buton>
                <Buton
                  gorunum="sessiz"
                  disabled={gonderiliyor}
                  onClick={() => {
                    setOnay(false);
                    setHata(null);
                  }}
                >
                  Vazgeç
                </Buton>
              </div>
            </section>
          ) : (
            <>
              <Buton boyut="genis" disabled={eksik > 0} onClick={onayAc}>
                Sipariş ver
              </Buton>
              {eksik > 0 && <p className="text-notr-700 mt-2 text-sm">{eksik} puan eksik</p>}
            </>
          )}
        </div>
      </Kart>
    </div>
  );
}

function SiparisSonucu({
  siparis,
  onGuncel,
  onMagaza,
  onSiparislerim,
}: {
  siparis: Siparis;
  onGuncel: (s: Siparis) => void;
  onMagaza: () => void;
  onSiparislerim?: () => void;
}) {
  const kok = useUsteKaydir();
  const sonGun = siparis.sonGun ? gunBicimi(siparis.sonGun) : null;

  return (
    <div ref={kok} className="flex flex-col gap-3">
      <Kart baslikSeviyesi={3} baslik="Siparişin alındı" ton="onay">
        <p className="text-notr-900 text-sm font-medium">{siparis.urunAdi}</p>
        <p className="text-notr-700 mt-1 text-sm">{siparis.puan} puan düştü.</p>

        {siparis.durum === "AWAITING_ADDRESS" && (
          <p className="text-notr-700 mt-2 text-sm">
            Kargo için adresini yaz{sonGun ? `; son gün ${sonGun}` : ""}. Süre dolarsa sipariş iptal
            olur ve puanın iade edilir.
          </p>
        )}
        {siparis.durum === "AWAITING_PARENT" && (
          <>
            <p className="text-notr-900 mt-2 text-sm font-medium">
              Veli onayı bekleniyor{sonGun ? `, son gün ${sonGun}` : ""}.
            </p>
            <p className="text-notr-700 mt-1 text-sm">
              Adresi velin verecek. Velin uygulamaya girip siparişi onaylayınca sipariş hazırlanmaya
              başlar. Süre dolarsa sipariş iptal olur ve puanın iade edilir.
            </p>
          </>
        )}
        {siparis.durum === "PREPARING" && (
          <p className="text-notr-900 mt-2 text-sm font-medium">
            Siparişin hazırlanıyor. Kargoya verilince haber veririz.
          </p>
        )}
        {siparis.durum !== "AWAITING_ADDRESS" &&
          siparis.durum !== "AWAITING_PARENT" &&
          siparis.durum !== "PREPARING" && (
            <p className="text-notr-900 mt-2 text-sm font-medium">
              {SIPARIS_DURUMU_ETIKETI[siparis.durum]}
            </p>
          )}
        {siparis.iptalEdilebilir && (
          <p className="text-notr-600 mt-2 text-xs">
            Kargoya verilene kadar siparişini Siparişlerim bölümünden iptal edebilirsin; puanın iade
            edilir.
          </p>
        )}
      </Kart>

      {siparis.durum === "AWAITING_ADDRESS" && (
        <Kart baslikSeviyesi={4} baslik="Teslimat adresin">
          <AdresBolumu siparis={siparis} onGuncel={onGuncel} />
        </Kart>
      )}

      <div className="flex flex-wrap gap-2">
        {onSiparislerim && <Buton onClick={onSiparislerim}>Siparişlerime git</Buton>}
        <Buton gorunum="ikincil" onClick={onMagaza}>
          Mağazaya dön
        </Buton>
      </div>
    </div>
  );
}
